/* Solvik — la sélection à comparer, partagée par tout le site.
 *
 * Ce fichier ne dessine pas de comparaison : il tient la liste des sols
 * retenus, câble les cases « Comparer » posées sur les vignettes et la fiche
 * produit, et dessine le plateau qui ramène à la page de comparaison.
 *
 * Il est chargé partout ; `solvik-comparateur.js`, lui, n'existe que sur la
 * page de comparaison et lit cet état par `window.SolvikComparer`.
 *
 * AUCUN BLOCAGE, JAMAIS
 * La version précédente désactivait toutes les cases dès trois sols retenus :
 * la seule sortie était d'aller sur la page de comparaison en retirer un. Un
 * cul-de-sac, et il tombait au pire moment — celui où le client vient de
 * trouver un quatrième décor qui lui plaît.
 *
 * Désormais le quatrième clic remplace le plus ancien. Ce n'est acceptable que
 * parce que le plateau montre les trois vignettes : le client VOIT la
 * substitution se produire, et « Annuler » la défait. Une substitution qu'on
 * voit n'est pas une disparition.
 *
 * LE STOCKAGE PEUT ÉCHOUER, ET CE N'EST PAS UNE HYPOTHÈSE D'ÉCOLE
 * En navigation privée ou quand le navigateur refuse les données de site,
 * lire ou écrire dans localStorage LÈVE une exception — l'accès lui-même,
 * pas seulement l'écriture. Tout passe donc par try/catch, et la sélection
 * retombe en mémoire vive : le comparateur reste utilisable pour la visite en
 * cours, il oublie simplement d'une page à l'autre.
 */

(function () {
  'use strict';

  var CLE = 'solvik:comparer';

  /* DEUX NOMBRES, ET ILS NE DISENT PAS LA MEME CHOSE.
   *
   * `CAPACITE` est ce que le stockage porte. Il ne bouge jamais : une sélection
   * faite sur un ordinateur ne doit pas se faire amputer parce qu'on a ouvert
   * le site sur un téléphone entre-temps.
   *
   * `max()` est ce qu'on accepte d'AJOUTER ici et maintenant. Sur un petit
   * écran il vaut 2, mesuré : à trois décors la page de comparaison donne des
   * visuels de 103 px et des colonnes de tableau de 75 px, où les valeurs
   * longues débordent sur la colonne voisine. À deux, 160 px et 100 px — la
   * plus longue valeur du catalogue tient.
   *
   * Le seuil est celui du reste du fichier de la page (749 px) : il faut que
   * la limite change au même moment que la mise en page, pas un pixel avant. */
  var CAPACITE = 3;
  var ETROIT = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;

  function max() {
    return (ETROIT && ETROIT.matches) ? 2 : CAPACITE;
  }

  // Le repli quand le disque est refusé. Il vit le temps de la page.
  var memoire = null;

  /* Le dernier remplacement, pour « Annuler ». Il ne survit volontairement pas
   * au rechargement : un remplacement qu'on ne conteste pas dans les secondes
   * qui suivent est un remplacement accepté. */
  var dernierRemplacement = null;

  /* Une entrée vaut { h: handle, d: décor, img: vignette }.
   *
   * Le format précédent ne stockait que le handle. Une sélection réelle était
   * en cours sur la boutique au moment du changement : la lecture tolère donc
   * l'ancien format plutôt que de la jeter. Une entrée sans décor s'affiche au
   * handle, et se complète d'elle-même dès qu'une case correspondante passe à
   * l'écran — voir `enrichir`. */
  function normaliser(v) {
    if (typeof v === 'string') return { h: v };
    if (v && typeof v === 'object' && typeof v.h === 'string') return v;
    return null;
  }

  function lire() {
    if (memoire) return memoire.slice();
    try {
      var brut = window.localStorage.getItem(CLE);
      var liste = brut ? JSON.parse(brut) : [];
      if (!Array.isArray(liste)) return [];
      return liste.map(normaliser).filter(Boolean).slice(0, CAPACITE);
    } catch (e) {
      return [];
    }
  }

  function ecrire(liste) {
    liste = liste.slice(0, CAPACITE);
    try {
      window.localStorage.setItem(CLE, JSON.stringify(liste));
      memoire = null;
    } catch (e) {
      memoire = liste.slice();
    }
    // Un seul évènement, écouté par les cases, le plateau et la page de
    // comparaison. Sans lui, cocher une case ne mettrait à jour que sa vignette.
    document.dispatchEvent(new CustomEvent('solvik:comparer', { detail: liste.slice() }));
    return liste;
  }

  function indexDe(liste, handle) {
    for (var i = 0; i < liste.length; i += 1) {
      if (liste[i].h === handle) return i;
    }
    return -1;
  }

  var API = {
    /* Pas de `MAX` exposé : un seul nombre porterait deux sens et mentirait
     * la moitié du temps. `capacite` est ce que le stockage tient, `max()`
     * ce qu'on accepte d'ajouter à cette taille d'écran. */
    capacite: CAPACITE,
    max: max,
    liste: lire,
    handles: function () { return lire().map(function (e) { return e.h; }); },
    contient: function (handle) { return indexDe(lire(), handle) !== -1; },
    plein: function () { return lire().length >= max(); },

    /* Renvoie { ajoute, remplace } :
     *   ajoute   true si le sol vient d'entrer, false s'il vient de sortir
     *   remplace l'entrée évincée quand la sélection était pleine, sinon null
     * Ne refuse jamais. */
    basculer: function (handle, infos) {
      var liste = lire();
      var i = indexDe(liste, handle);
      if (i !== -1) {
        liste.splice(i, 1);
        ecrire(liste);
        return { ajoute: false, remplace: null };
      }
      var neuf = { h: handle };
      if (infos && infos.d) neuf.d = infos.d;
      if (infos && infos.img) neuf.img = infos.img;

      var evince = null;
      if (liste.length >= max()) {
        // Le plus ancien sort : c'est celui dont l'intérêt est le plus daté.
        evince = liste.shift();
        dernierRemplacement = { evince: evince, neuf: neuf };
      } else {
        dernierRemplacement = null;
      }
      liste.push(neuf);
      ecrire(liste);
      return { ajoute: true, remplace: evince };
    },

    retirer: function (handle) {
      var liste = lire();
      var i = indexDe(liste, handle);
      if (i === -1) return;
      liste.splice(i, 1);
      // Un retrait volontaire périme l'annulation : « X a remplacé Y » ne veut
      // plus rien dire une fois qu'on a retiré un troisième sol à la main, et
      // le bouton rétablirait un état que personne n'a demandé.
      dernierRemplacement = null;
      ecrire(liste);
    },

    /* Défait le dernier remplacement : le sol évincé revient à sa place, celui
     * qui l'avait chassé repart. */
    annuler: function () {
      if (!dernierRemplacement) return false;
      var liste = lire();
      var i = indexDe(liste, dernierRemplacement.neuf.h);
      if (i !== -1) liste.splice(i, 1);
      liste.unshift(dernierRemplacement.evince);
      dernierRemplacement = null;
      ecrire(liste);
      return true;
    },

    /* Complète le nom et la vignette d'une entrée. Sert à la page de
     * comparaison, qui porte le catalogue complet et connaît donc le décor
     * exact — là où le plateau, lui, n'a que ce qu'il a pu glaner. Sans elle,
     * un nom dépanné par le réseau resterait long jusqu'à ce que le visiteur
     * retombe par hasard sur la bonne grille. */
    decrire: function (handle, infos) {
      if (!infos || !infos.d) return;
      var liste = lire();
      var i = indexDe(liste, handle);
      if (i === -1) return;
      if (liste[i].d === infos.d && liste[i].img === infos.img) return;
      liste[i] = { h: handle, d: infos.d, img: infos.img || liste[i].img || '' };
      ecrire(liste);
    },

    remplacementEnCours: function () { return dernierRemplacement; },

    vider: function () { dernierRemplacement = null; ecrire([]); }
  };

  window.SolvikComparer = API;

  /* Franchir 750 px change `max()`, donc le nombre de décors que la page de
   * comparaison affiche et l'état des cases. Rien n'est écrit ici : on
   * réémet seulement l'évènement, et chaque écouteur se redessine avec la
   * même liste qu'avant. Une rotation d'écran ne détruit donc aucune
   * sélection, elle change seulement ce qu'on en montre. */
  if (ETROIT) {
    var reagir = function () {
      document.dispatchEvent(new CustomEvent('solvik:comparer', { detail: lire() }));
    };
    if (typeof ETROIT.addEventListener === 'function') ETROIT.addEventListener('change', reagir);
    else if (typeof ETROIT.addListener === 'function') ETROIT.addListener(reagir);
  }

  /* Les cases posées sur les vignettes et la fiche ------------------------ */

  function infosDe(champ) {
    return {
      d: champ.getAttribute('data-sv-decor') || '',
      img: champ.getAttribute('data-sv-image') || ''
    };
  }

  /* Une entrée héritée de l'ancien format n'a ni décor ni vignette. Dès qu'une
   * case du même sol est présente sur la page, on complète l'entrée en silence :
   * le plateau cesse alors d'afficher un handle brut. */
  function enrichir() {
    var liste = lire();
    var change = false;
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-sv-comparer]'),
      function (champ) {
        var i = indexDe(liste, champ.getAttribute('data-sv-comparer'));
        if (i === -1) return;
        var infos = infosDe(champ);
        if (!infos.d) return;
        /* La valeur du serveur fait autorité, toujours — pas seulement quand
         * l'entrée est vide. C'est ce qui relève un nom dépanné par le réseau :
         * « Chêne de Nouvelle-Angleterre » vaut mieux que « Sol stratifié Pergo
         * Perstorp Chêne beige naturel 8 mm » dans une tuile de 3,6 rem et dans
         * la phrase de remplacement. Et cela répare aussi ce qui a été écrit
         * avant l'existence de cette règle, sans drapeau à maintenir. */
        if (liste[i].d === infos.d && liste[i].img === infos.img) return;
        liste[i] = { h: liste[i].h, d: infos.d, img: infos.img };
        change = true;
      }
    );
    if (change) ecrire(liste);
  }

  /* Le repli quand la page ne contient aucune case du sol concerné.
   *
   * `enrichir` ne peut compléter que ce qui est à l'écran. Une sélection
   * héritée de l'ancien format s'affichait donc en handles bruts tant que le
   * visiteur ne retombait pas sur les bonnes fiches — ce qui n'arrive jamais
   * pour trois sols de trois familles différentes.
   *
   * `/products/{handle}.js` donne le titre et l'image sans jeton ni metafield.
   * Trois requêtes légères, une seule fois dans la vie de la sélection : les
   * entrées réparées sont réécrites, et la condition `!entree.d` ne se
   * représente plus. Un échec réseau laisse simplement la tuile en l'état. */
  var reparations = {};

  function reparer() {
    var manquants = lire().filter(function (e) { return !e.d && !reparations[e.h]; });
    if (!manquants.length || typeof window.fetch !== 'function') return;

    manquants.forEach(function (e) { reparations[e.h] = true; });

    Promise.all(manquants.map(function (e) {
      return window.fetch('/products/' + encodeURIComponent(e.h) + '.js')
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (p) {
          if (!p) return null;
          return {
            h: e.h,
            // Le titre complet, faute de mieux : `/products/x.js` ne renvoie
            // pas les metafields, donc pas le décor. `enrichir` relèvera cette
            // valeur dès qu'une case du même sol passera à l'écran.
            d: p.title || e.h,
            img: p.featured_image ? p.featured_image + '&width=120' : ''
          };
        })
        .catch(function () { return null; });
    })).then(function (repares) {
      var utiles = repares.filter(Boolean);
      if (!utiles.length) return;
      var liste = lire();
      var change = false;
      utiles.forEach(function (r) {
        var i = indexDe(liste, r.h);
        if (i !== -1 && !liste[i].d) { liste[i] = r; change = true; }
      });
      if (change) ecrire(liste);
    });
  }

  function etatCase(champ) {
    var coche = API.contient(champ.getAttribute('data-sv-comparer'));
    champ.checked = coche;
    var enveloppe = champ.closest('[data-sv-comparer-boite]');
    if (enveloppe) enveloppe.classList.toggle('sv-comparer--actif', coche);
  }

  function cabler(champ) {
    if (champ.dataset.svCable) return;
    champ.dataset.svCable = '1';
    champ.addEventListener('change', function () {
      API.basculer(champ.getAttribute('data-sv-comparer'), infosDe(champ));
    });
  }

  function rafraichirCases() {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-sv-comparer]'),
      function (champ) { cabler(champ); etatCase(champ); }
    );
  }

  /* Le plateau, en bas de l'écran ----------------------------------------- */

  function texte(barre, cle, remplacements) {
    var s = barre.getAttribute('data-texte-' + cle) || '';
    Object.keys(remplacements || {}).forEach(function (k) {
      s = s.split('{' + k + '}').join(remplacements[k]);
    });
    return s;
  }

  function nomLisible(entree) {
    // Un handle en dernier recours : « sol-stratifie-pergo-visby-... » se lit
    // mal, mais reste plus utile qu'une tuile muette.
    return entree.d || entree.h;
  }

  function dessinerTuiles(barre) {
    var boite = barre.querySelector('[data-sv-barre-tuiles]');
    if (!boite) return;
    boite.textContent = '';
    API.liste().forEach(function (entree) {
      var nom = nomLisible(entree);
      var li = document.createElement('li');
      li.className = 'sv-barre__tuile';

      if (entree.img) {
        var img = document.createElement('img');
        img.src = entree.img;
        img.alt = nom;
        li.appendChild(img);
      } else {
        var pastille = document.createElement('span');
        pastille.className = 'sv-barre__tuile-texte';
        pastille.textContent = nom.slice(0, 2).toUpperCase();
        li.appendChild(pastille);
      }

      var croix = document.createElement('button');
      croix.type = 'button';
      croix.className = 'sv-barre__croix';
      croix.setAttribute('aria-label', texte(barre, 'retirer', { nom: nom }));
      croix.setAttribute('title', texte(barre, 'retirer', { nom: nom }));
      croix.textContent = '×';
      croix.addEventListener('click', function () { API.retirer(entree.h); });
      li.appendChild(croix);

      boite.appendChild(li);
    });
  }

  function rafraichirBarre() {
    var barre = document.querySelector('[data-sv-barre]');
    if (!barre) return;
    var n = API.liste().length;
    barre.hidden = n === 0;

    /* Le plateau est `position: fixed` : il flotte au-dessus de la page et ne
     * pousse rien. En bas de page il recouvrait donc 72 px de contenu SANS
     * qu'on puisse défiler plus loin — mesuré à 375 px, sur les liens légaux
     * et la ligne de copyright, et sur toutes les pages du site puisque le
     * plateau y est rendu. La classe réserve la hauteur correspondante ; le
     * CSS en tire un `padding-bottom`. */
    document.documentElement.classList.toggle('sv-barre-visible', n > 0);

    dessinerTuiles(barre);

    var compte = barre.querySelector('[data-sv-barre-compte]');
    if (compte) {
      compte.textContent = texte(barre, n > 1 ? 'pluriel' : 'singulier', { n: n });
    }

    // On ne propose de comparer qu'à partir de deux : un sol seul n'a rien
    // en face de lui, et le bouton mènerait à une page qui redemande.
    var lien = barre.querySelector('[data-sv-barre-lien]');
    if (lien) lien.hidden = n < 2;
  }

  function afficherRemplacement(barre, evince, neuf) {
    var zone = barre.querySelector('[data-sv-barre-message]');
    var cible = barre.querySelector('[data-sv-barre-message-texte]');
    if (!zone || !cible) return;
    cible.textContent = texte(barre, 'remplace', {
      neuf: nomLisible(neuf), ancien: nomLisible(evince)
    });
    zone.hidden = false;
  }

  function cacherRemplacement(barre) {
    var zone = barre.querySelector('[data-sv-barre-message]');
    if (zone) zone.hidden = true;
  }

  function demarrer() {
    enrichir();
    rafraichirCases();
    rafraichirBarre();
    // Après le premier rendu : la réparation est asynchrone et redessinera le
    // plateau quand elle aboutira. Rien ne doit attendre le réseau.
    reparer();

    var barre = document.querySelector('[data-sv-barre]');
    if (barre) {
      var vider = barre.querySelector('[data-sv-barre-vider]');
      if (vider) vider.addEventListener('click', function () { API.vider(); });

      var annuler = barre.querySelector('[data-sv-barre-annuler]');
      if (annuler) annuler.addEventListener('click', function () { API.annuler(); });
    }

    /* Une case cochée déclenche `basculer`, qui écrit et émet l'évènement. Le
     * message de remplacement se lit donc ici, après coup, plutôt qu'au point
     * de clic — de cette façon il s'affiche aussi quand le remplacement vient
     * du sélecteur de la page de comparaison. */
    document.addEventListener('solvik:comparer', function () {
      rafraichirCases();
      rafraichirBarre();
      if (!barre) return;
      var r = API.remplacementEnCours();
      if (r) afficherRemplacement(barre, r.evince, r.neuf);
      else cacherRemplacement(barre);
    });

    // Le panier et les filtres de Dawn remplacent des morceaux de page : les
    // cases réapparaissent alors sans être câblées.
    document.addEventListener('shopify:section:load', function () {
      rafraichirCases();
      enrichir();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', demarrer);
  } else {
    demarrer();
  }
})();
